import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const emptyProduct = {
    slug: "",
    title_en: "",
    title_es: "",
    description_en: "",
    description_es: "",
    image: "",
    category: "general",
    span: "col-span-12 md:col-span-6",
    order: 0,
};

const emptyTestimonial = {
    author: "",
    quote_en: "",
    quote_es: "",
    occasion: "",
    order: 0,
};

const TABS = ["settings", "products", "gallery", "testimonials", "bookings", "messages"];

const Admin = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("products");
    const [products, setProducts] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [settings, setSettings] = useState(null);
    const [messages, setMessages] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [galleryItems, setGalleryItems] = useState([]);
    const [editingProduct, setEditingProduct] = useState(null);
    const [editingTestimonial, setEditingTestimonial] = useState(null);
    const [editingGallery, setEditingGallery] = useState(null);

    const loadAll = useCallback(async () => {
        try {
            const [p, t, s, m, b, g] = await Promise.all([
                api.get("/products"),
                api.get("/testimonials"),
                api.get("/settings"),
                api.get("/admin/messages"),
                api.get("/admin/bookings"),
                api.get("/gallery"),
            ]);
            setProducts(p.data);
            setTestimonials(t.data);
            setSettings(s.data);
            setMessages(m.data);
            setBookings(b.data);
            setGalleryItems(g.data);
        } catch (_e) {
            toast.error("Failed to load data");
        }
    }, []);

    useEffect(() => {
        if (user === null) navigate("/login");
        if (user) loadAll();
    }, [user, navigate, loadAll]);

    if (user === undefined) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
                <div className="font-body text-sm uppercase tracking-[0.3em]" style={{ color: "var(--ink-mute)" }}>Loading…</div>
            </div>
        );
    }
    if (!user) return null;

    return (
        <div data-testid={ADMIN.root} className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
            {/* Top bar */}
            <header className="sticky top-0 z-30 backdrop-blur-xl border-b" style={{ background: "rgba(250,248,245,0.85)", borderColor: "rgba(46,16,37,0.1)" }}>
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-6">
                        <Link to="/" className="font-serif text-xl">
                            Piñatas <em className="font-hand text-2xl" style={{ color: "var(--pink)" }}>con Karina</em>
                        </Link>
                        <span className="font-body text-[10px] uppercase tracking-[0.28em] px-3 py-1 rounded-full" style={{ background: "#FFE1EA", color: "#8A0E39" }}>
                            Studio
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="font-body text-xs" style={{ color: "var(--ink-mute)" }}>
                            {user?.email}
                        </div>
                        <button
                            data-testid={ADMIN.logout}
                            onClick={async () => {
                                await logout();
                                navigate("/");
                            }}
                            className="btn-outline px-4 py-2 rounded-full text-xs font-semibold tracking-wide"
                        >
                            Log out
                        </button>
                    </div>
                </div>
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-3 flex gap-2 md:gap-4 flex-wrap">
                    {TABS.map((k) => (
                        <button
                            key={k}
                            data-testid={ADMIN[`tab${k[0].toUpperCase()}${k.slice(1)}`]}
                            onClick={() => setTab(k)}
                            className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-semibold transition-colors"
                            style={{
                                background: tab === k ? "var(--ink-1)" : "transparent",
                                color: tab === k ? "#fff" : "var(--ink-1)",
                                border: "1px solid var(--ink-1)",
                            }}
                        >
                            {k}
                        </button>
                    ))}
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14">
                {tab === "settings" && (
                    <SettingsPanel settings={settings} onSaved={loadAll} />
                )}

                {tab === "products" && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Products & Services</h1>
                            <button
                                data-testid={ADMIN.productAddBtn}
                                onClick={() => setEditingProduct({ ...emptyProduct })}
                                className="btn-pink px-5 py-2.5 rounded-full text-xs font-semibold tracking-wide"
                            >
                                + Add product
                            </button>
                        </div>

                        {editingProduct && (
                            <ProductEditor
                                initial={editingProduct}
                                onCancel={() => setEditingProduct(null)}
                                onSaved={async () => {
                                    setEditingProduct(null);
                                    await loadAll();
                                }}
                            />
                        )}

                        <div data-testid={ADMIN.productList} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {products.map((p) => (
                                <div key={p.id} className="p-5 rounded-2xl bg-white border" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
                                    <div className="relative aspect-video overflow-hidden rounded-xl mb-4" style={{ background: "#FFE4EE" }}>
                                        {p.image && <img src={p.image} alt={p.title_en} className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="font-serif text-2xl leading-tight">{p.title_en}</div>
                                    <div className="font-body text-xs uppercase tracking-[0.24em] mt-1" style={{ color: "var(--ink-mute)" }}>
                                        {p.category} · {p.slug}
                                    </div>
                                    <div className="mt-4 flex gap-2">
                                        <button
                                            data-testid={ADMIN.productEditBtn(p.id)}
                                            onClick={() => setEditingProduct(p)}
                                            className="btn-outline px-4 py-2 rounded-full text-xs font-semibold"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            data-testid={ADMIN.productDeleteBtn(p.id)}
                                            onClick={async () => {
                                                if (!window.confirm("Delete this item?")) return;
                                                try {
                                                    await api.delete(`/admin/products/${p.id}`);
                                                    toast.success("Deleted");
                                                    await loadAll();
                                                } catch (_e) {
                                                    toast.error("Failed to delete");
                                                }
                                            }}
                                            className="px-4 py-2 rounded-full text-xs font-semibold border"
                                            style={{ borderColor: "rgba(232,58,116,0.5)", color: "var(--pink)" }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "testimonials" && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Testimonials</h1>
                            <button
                                data-testid={ADMIN.testimonialAddBtn}
                                onClick={() => setEditingTestimonial({ ...emptyTestimonial })}
                                className="btn-pink px-5 py-2.5 rounded-full text-xs font-semibold tracking-wide"
                            >
                                + Add testimonial
                            </button>
                        </div>

                        {editingTestimonial && (
                            <TestimonialEditor
                                initial={editingTestimonial}
                                onCancel={() => setEditingTestimonial(null)}
                                onSaved={async () => {
                                    setEditingTestimonial(null);
                                    await loadAll();
                                }}
                            />
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {testimonials.map((tt) => (
                                <div key={tt.id} className="p-6 rounded-2xl bg-white border" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
                                    <div className="font-serif text-xl leading-snug">{tt.quote_en}</div>
                                    <div className="font-body text-xs uppercase tracking-[0.24em] mt-3" style={{ color: "var(--ink-mute)" }}>
                                        {tt.author} · {tt.occasion}
                                    </div>
                                    <div className="mt-4 flex gap-2">
                                        <button
                                            onClick={() => setEditingTestimonial(tt)}
                                            className="btn-outline px-4 py-2 rounded-full text-xs font-semibold"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={async () => {
                                                if (!window.confirm("Delete this testimonial?")) return;
                                                await api.delete(`/admin/testimonials/${tt.id}`);
                                                await loadAll();
                                            }}
                                            className="px-4 py-2 rounded-full text-xs font-semibold border"
                                            style={{ borderColor: "rgba(232,58,116,0.5)", color: "var(--pink)" }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "gallery" && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Gallery</h1>
                            <button
                                data-testid={ADMIN.galleryAddBtn}
                                onClick={() => setEditingGallery({ image: "", caption_en: "", caption_es: "", theme: "", order: (galleryItems.length + 1) })}
                                className="btn-pink px-5 py-2.5 rounded-full text-xs font-semibold tracking-wide"
                            >
                                + Add photo
                            </button>
                        </div>

                        {editingGallery && (
                            <GalleryEditor
                                initial={editingGallery}
                                onCancel={() => setEditingGallery(null)}
                                onSaved={async () => {
                                    setEditingGallery(null);
                                    await loadAll();
                                }}
                            />
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {galleryItems.map((g) => (
                                <div key={g.id} className="rounded-2xl bg-white border overflow-hidden" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
                                    <div className="relative aspect-square" style={{ background: "#FFE4EE" }}>
                                        {g.image && <img src={g.image} alt={g.caption_en} className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="p-4">
                                        <div className="font-body text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ink-mute)" }}>
                                            #{g.order} · {g.theme}
                                        </div>
                                        <div className="font-serif text-lg leading-tight mt-1 line-clamp-2">{g.caption_en}</div>
                                        <div className="mt-3 flex gap-2">
                                            <button
                                                onClick={() => setEditingGallery(g)}
                                                className="btn-outline px-3 py-1.5 rounded-full text-[11px] font-semibold"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                data-testid={ADMIN.galleryDeleteBtn(g.id)}
                                                onClick={async () => {
                                                    if (!window.confirm("Delete this photo?")) return;
                                                    try {
                                                        await api.delete(`/admin/gallery/${g.id}`);
                                                        toast.success("Deleted");
                                                        await loadAll();
                                                    } catch (_e) {
                                                        toast.error("Failed to delete");
                                                    }
                                                }}
                                                className="px-3 py-1.5 rounded-full text-[11px] font-semibold border"
                                                style={{ borderColor: "rgba(232,58,116,0.5)", color: "var(--pink)" }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "bookings" && (
                    <div className="space-y-6">
                        <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Bookings</h1>
                        {bookings.length === 0 && (
                            <div className="p-8 rounded-2xl border font-body text-sm" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
                                No bookings yet.
                            </div>
                        )}
                        <div className="space-y-4">
                            {bookings.map((b) => {
                                const statusColor =
                                    b.status === "confirmed" ? { bg: "#DFF6F3", fg: "#0B7A75" }
                                    : b.status === "declined" ? { bg: "#F5E1E1", fg: "#8A0E39" }
                                    : { bg: "#FFF3D6", fg: "#8A6B00" };
                                return (
                                    <div key={b.id} className="p-6 rounded-2xl bg-white border" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
                                        <div className="flex items-start justify-between flex-wrap gap-3">
                                            <div>
                                                <div className="font-body text-[10px] uppercase tracking-[0.24em] mb-1" style={{ color: "var(--ink-mute)" }}>
                                                    {b.product_title || "Studio booking"} · {b.event_date} · {b.start_time}–{b.end_time}
                                                </div>
                                                <div className="font-serif text-2xl leading-tight">{b.customer_name}</div>
                                                <div className="font-body text-sm mt-1" style={{ color: "var(--ink-2)" }}>
                                                    {b.phone}{b.email ? ` · ${b.email}` : ""}{b.occasion ? ` · ${b.occasion}` : ""}
                                                </div>
                                                {b.message && (
                                                    <div className="font-body text-sm mt-3 max-w-xl" style={{ color: "var(--ink-2)" }}>
                                                        “{b.message}”
                                                    </div>
                                                )}
                                            </div>
                                            <span className="font-body text-[10px] uppercase tracking-[0.24em] px-3 py-1 rounded-full" style={{ background: statusColor.bg, color: statusColor.fg }}>
                                                {b.status}
                                            </span>
                                        </div>
                                        <div className="mt-4 flex gap-2 flex-wrap">
                                            <button
                                                data-testid={ADMIN.bookingConfirm(b.id)}
                                                onClick={async () => {
                                                    try {
                                                        await api.patch(`/admin/bookings/${b.id}`, { status: "confirmed" });
                                                        toast.success("Confirmed");
                                                        await loadAll();
                                                    } catch (_e) {
                                                        toast.error("Failed");
                                                    }
                                                }}
                                                className="btn-outline px-4 py-2 rounded-full text-xs font-semibold"
                                            >
                                                Confirm
                                            </button>
                                            <button
                                                data-testid={ADMIN.bookingDecline(b.id)}
                                                onClick={async () => {
                                                    try {
                                                        await api.patch(`/admin/bookings/${b.id}`, { status: "declined" });
                                                        toast.success("Declined");
                                                        await loadAll();
                                                    } catch (_e) {
                                                        toast.error("Failed");
                                                    }
                                                }}
                                                className="px-4 py-2 rounded-full text-xs font-semibold border"
                                                style={{ borderColor: "rgba(232,58,116,0.5)", color: "var(--pink)" }}
                                            >
                                                Decline
                                            </button>
                                            <button
                                                data-testid={ADMIN.bookingDelete(b.id)}
                                                onClick={async () => {
                                                    if (!window.confirm("Delete this booking?")) return;
                                                    await api.delete(`/admin/bookings/${b.id}`);
                                                    await loadAll();
                                                }}
                                                className="px-4 py-2 rounded-full text-xs font-semibold"
                                                style={{ color: "var(--ink-mute)" }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {tab === "messages" && (
                    <div className="space-y-6">
                        <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Inbox</h1>
                        {messages.length === 0 && (
                            <div className="p-8 rounded-2xl border font-body text-sm" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
                                No messages yet.
                            </div>
                        )}
                        <div className="space-y-4">
                            {messages.map((m) => (
                                <div key={m.id} className="p-6 rounded-2xl bg-white border" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
                                    <div className="flex items-center justify-between font-body text-xs uppercase tracking-[0.24em]" style={{ color: "var(--ink-mute)" }}>
                                        <span>{m.name} · {m.email || m.phone}</span>
                                        <span>{new Date(m.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="mt-2 font-serif text-lg leading-snug">{m.message}</div>
                                    {m.occasion && <div className="mt-2 font-body text-xs" style={{ color: "var(--ink-mute)" }}>Occasion: {m.occasion}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

/* -------------------- Product editor -------------------- */
const ProductEditor = ({ initial, onCancel, onSaved }) => {
    const [form, setForm] = useState({ ...initial });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            if (form.id) {
                await api.put(`/admin/products/${form.id}`, form);
            } else {
                await api.post("/admin/products", form);
            }
            toast.success("Saved");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="p-6 md:p-8 rounded-2xl bg-white border" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
            <h2 className="font-serif text-2xl mb-6">{form.id ? "Edit product" : "New product"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField label="Slug" value={form.slug} onChange={(v) => set("slug", v)} />
                <TextField label="Category" value={form.category} onChange={(v) => set("category", v)} />
                <TextField label="Title (English)" value={form.title_en} onChange={(v) => set("title_en", v)} />
                <TextField label="Title (Spanish)" value={form.title_es} onChange={(v) => set("title_es", v)} />
                <TextField label="Description (English)" value={form.description_en} onChange={(v) => set("description_en", v)} area />
                <TextField label="Description (Spanish)" value={form.description_es} onChange={(v) => set("description_es", v)} area />
                <TextField label="Image URL" value={form.image} onChange={(v) => set("image", v)} full />
                <TextField label="Grid span (tailwind classes)" value={form.span} onChange={(v) => set("span", v)} />
                <TextField label="Order" type="number" value={form.order} onChange={(v) => set("order", Number(v))} />
            </div>
            <div className="mt-6 flex gap-3">
                <button
                    data-testid={ADMIN.productSaveBtn}
                    disabled={busy}
                    onClick={save}
                    className="btn-pink px-6 py-3 rounded-full text-xs font-semibold tracking-wide disabled:opacity-60"
                >
                    {busy ? "Saving…" : "Save product"}
                </button>
                <button onClick={onCancel} className="btn-outline px-6 py-3 rounded-full text-xs font-semibold tracking-wide">
                    Cancel
                </button>
            </div>
        </div>
    );
};

const TestimonialEditor = ({ initial, onCancel, onSaved }) => {
    const [form, setForm] = useState({ ...initial });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            if (form.id) {
                await api.put(`/admin/testimonials/${form.id}`, form);
            } else {
                await api.post("/admin/testimonials", form);
            }
            toast.success("Saved");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="p-6 md:p-8 rounded-2xl bg-white border" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
            <h2 className="font-serif text-2xl mb-6">{form.id ? "Edit testimonial" : "New testimonial"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField label="Author" value={form.author} onChange={(v) => set("author", v)} />
                <TextField label="Occasion" value={form.occasion} onChange={(v) => set("occasion", v)} />
                <TextField label="Quote (English)" value={form.quote_en} onChange={(v) => set("quote_en", v)} area full />
                <TextField label="Quote (Spanish)" value={form.quote_es} onChange={(v) => set("quote_es", v)} area full />
                <TextField label="Order" type="number" value={form.order} onChange={(v) => set("order", Number(v))} />
            </div>
            <div className="mt-6 flex gap-3">
                <button disabled={busy} onClick={save} className="btn-pink px-6 py-3 rounded-full text-xs font-semibold tracking-wide disabled:opacity-60">
                    {busy ? "Saving…" : "Save"}
                </button>
                <button onClick={onCancel} className="btn-outline px-6 py-3 rounded-full text-xs font-semibold tracking-wide">
                    Cancel
                </button>
            </div>
        </div>
    );
};

const GalleryEditor = ({ initial, onCancel, onSaved }) => {
    const [form, setForm] = useState({ ...initial });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const save = async () => {
        if (!form.image?.trim()) {
            toast.error("Image URL is required");
            return;
        }
        setBusy(true);
        try {
            if (form.id) {
                await api.put(`/admin/gallery/${form.id}`, form);
            } else {
                await api.post("/admin/gallery", form);
            }
            toast.success("Saved");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="p-6 md:p-8 rounded-2xl bg-white border" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
            <h2 className="font-serif text-2xl mb-6">{form.id ? "Edit photo" : "New photo"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField label="Image URL" value={form.image} onChange={(v) => set("image", v)} full />
                <TextField label="Theme" value={form.theme} onChange={(v) => set("theme", v)} />
                <TextField label="Order" type="number" value={form.order} onChange={(v) => set("order", Number(v))} />
                <TextField label="Caption (English)" value={form.caption_en} onChange={(v) => set("caption_en", v)} area full />
                <TextField label="Caption (Spanish)" value={form.caption_es} onChange={(v) => set("caption_es", v)} area full />
            </div>
            <div className="mt-6 flex gap-3">
                <button disabled={busy} onClick={save} className="btn-pink px-6 py-3 rounded-full text-xs font-semibold tracking-wide disabled:opacity-60">
                    {busy ? "Saving…" : "Save photo"}
                </button>
                <button onClick={onCancel} className="btn-outline px-6 py-3 rounded-full text-xs font-semibold tracking-wide">
                    Cancel
                </button>
            </div>
        </div>
    );
};


const SettingsPanel = ({ settings, onSaved }) => {
    const [form, setForm] = useState(settings || {});
    const [busy, setBusy] = useState(false);
    useEffect(() => setForm(settings || {}), [settings]);
    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/admin/settings", form);
            toast.success("Settings saved");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    if (!settings) return null;

    return (
        <div className="space-y-6">
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Site content</h1>
            <div className="p-6 md:p-8 rounded-2xl bg-white border" style={{ borderColor: "rgba(46,16,37,0.1)" }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField label="Hero kicker (EN)" value={form.hero_kicker_en} onChange={(v) => set("hero_kicker_en", v)} />
                    <TextField label="Hero kicker (ES)" value={form.hero_kicker_es} onChange={(v) => set("hero_kicker_es", v)} />
                    <TextField label="Hero line 1 (EN)" value={form.hero_line1_en} onChange={(v) => set("hero_line1_en", v)} />
                    <TextField label="Hero line 1 (ES)" value={form.hero_line1_es} onChange={(v) => set("hero_line1_es", v)} />
                    <TextField label="Hero line 2 (EN)" value={form.hero_line2_en} onChange={(v) => set("hero_line2_en", v)} />
                    <TextField label="Hero line 2 (ES)" value={form.hero_line2_es} onChange={(v) => set("hero_line2_es", v)} />
                    <TextField label="Hero line 3 (EN)" value={form.hero_line3_en} onChange={(v) => set("hero_line3_en", v)} />
                    <TextField label="Hero line 3 (ES)" value={form.hero_line3_es} onChange={(v) => set("hero_line3_es", v)} />
                    <TextField label="Tagline (EN)" value={form.tagline_en} onChange={(v) => set("tagline_en", v)} area full />
                    <TextField label="Tagline (ES)" value={form.tagline_es} onChange={(v) => set("tagline_es", v)} area full />
                    <TextField label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
                    <TextField label="WhatsApp digits" value={form.whatsapp} onChange={(v) => set("whatsapp", v)} />
                    <TextField label="WhatsApp display" value={form.whatsapp_display} onChange={(v) => set("whatsapp_display", v)} />
                    <TextField label="Email" value={form.email} onChange={(v) => set("email", v)} />
                    <TextField label="Address (EN)" value={form.address_en} onChange={(v) => set("address_en", v)} full />
                    <TextField label="Address (ES)" value={form.address_es} onChange={(v) => set("address_es", v)} full />
                </div>
                <div className="mt-6">
                    <button
                        data-testid={ADMIN.settingsSaveBtn}
                        disabled={busy}
                        onClick={save}
                        className="btn-pink px-6 py-3 rounded-full text-xs font-semibold tracking-wide disabled:opacity-60"
                    >
                        {busy ? "Saving…" : "Save settings"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const TextField = ({ label, value, onChange, type = "text", area, full }) => (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
        <span className="font-body text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ink-mute)" }}>{label}</span>
        {area ? (
            <textarea
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                rows={3}
                className="mt-2 w-full rounded-md p-3 font-body text-sm border resize-none focus:outline-none focus:border-[var(--pink)]"
                style={{ borderColor: "rgba(46,16,37,0.15)", background: "#FFF8FA", color: "var(--ink-1)" }}
            />
        ) : (
            <input
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="mt-2 w-full rounded-md p-3 font-body text-sm border focus:outline-none focus:border-[var(--pink)]"
                style={{ borderColor: "rgba(46,16,37,0.15)", background: "#FFF8FA", color: "var(--ink-1)" }}
            />
        )}
    </label>
);

export default Admin;
